import React from 'react';
import { envVariables } from '@fabric-ui/utils';
import { QueryClient, QueryClientConfig, QueryClientProvider } from '@tanstack/react-query';
import { useAvDebugContext } from './AvDebugProvider';
import { GraphQLClientProvider } from './GraphQLClientProvider';
import { useNetworkStatus } from './NetworkStatusContext';

const ReactQueryDevtools =
  envVariables.MODE === 'production'
    ? () => null // Render nothing in production
    : React.lazy(() =>
        import('@tanstack/react-query-devtools/build/modern/production.js').then(d => ({
          default: d.ReactQueryDevtools,
        }))
      );

const defaultOptions = {
  queries: {
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
    retry: false,
  },
} as const satisfies QueryClientConfig['defaultOptions'];

export const client = new QueryClient({
  defaultOptions,
});

export default function AvQueryProvider({
  children,
  queryClient = client,
}: React.PropsWithChildren<{ queryClient?: QueryClient }> = {}): React.ReactNode {
  const avDebugCtx = useAvDebugContext();
  const { isOnline } = useNetworkStatus();
  queryClient.setDefaultOptions({ ...defaultOptions, queries: { ...defaultOptions.queries, enabled: isOnline } });

  return (
    <GraphQLClientProvider>
      <QueryClientProvider client={queryClient}>
        {children}
        {avDebugCtx?.showDevTools && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </GraphQLClientProvider>
  );
}
